import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, setToken } from "@/lib/api";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || "").join(" ");
  return "Niečo sa pokazilo, skúste znova.";
};

export default function AuthLogin({ role = "admin", title, onSuccess, footer = null }) {
  const [step, setStep] = useState("cred");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submitCred = async () => {
    if (!email || !password) return toast.error("Zadajte e-mail a heslo.");
    setBusy(true);
    try {
      await api.post("/auth/login", { email, password });
      setStep("otp");
      toast.success("Kód sme poslali na váš e-mail.");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const submitOtp = async () => {
    if (code.length < 4) return toast.error("Zadajte kód z e-mailu.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code });
      if (data.user.role !== role) { toast.error("Tento účet nemá potrebné oprávnenie."); setBusy(false); return; }
      setToken(data.token);
      onSuccess(data.user);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-ink px-5 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm border border-white/10 bg-ink-2 p-8">
        <div className="w-12 h-12 bg-gold flex items-center justify-center mb-6">
          {step === "cred" ? <Lock className="w-6 h-6 text-ink" /> : <ShieldCheck className="w-6 h-6 text-ink" />}
        </div>
        <h1 className="font-display text-4xl text-white mb-1">{title || (role === "admin" ? "Admin" : "Partner")}</h1>
        <p className="text-white/50 text-sm mb-6">
          {step === "cred" ? "Zateplenia.eu — prihlásenie" : `Zadajte 6-miestny kód z e-mailu ${email}`}
        </p>

        {step === "cred" ? (
          <>
            <div className="relative mb-3">
              <Mail className="w-4 h-4 text-gold absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail"
                data-testid="login-email" className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white pl-10 pr-4 py-3" />
            </div>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitCred()} placeholder="Heslo" data-testid="login-password"
              className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3 mb-4" />
            <button onClick={submitCred} disabled={busy} data-testid="login-submit"
              className="w-full bg-gold text-ink py-3 font-bold uppercase tracking-wider hover:bg-gold-light flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pokračovať <ArrowRight className="w-4 h-4" /></>}
            </button>
          </>
        ) : (
          <>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => e.key === "Enter" && submitOtp()} placeholder="______" data-testid="otp-input"
              className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white text-center text-3xl font-mono2 tracking-[0.5em] px-4 py-3 mb-4" />
            <button onClick={submitOtp} disabled={busy} data-testid="otp-submit"
              className="w-full bg-gold text-ink py-3 font-bold uppercase tracking-wider hover:bg-gold-light flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Overiť a prihlásiť"}
            </button>
            <button onClick={() => setStep("cred")} className="w-full text-white/40 text-xs mt-3 hover:text-white">← Späť</button>
          </>
        )}
      </motion.div>
      {footer ? <div className="w-full max-w-sm mt-6">{footer}</div> : null}
    </div>
  );
}
